import React, { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { documentService } from '../../services/document.service'
import { Document } from '../../types'
import './DocumentPreview.scss'

const DocumentPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const documentId = parseInt(id || '0', 10)

  const [document, setDocument] = useState<Document | null>(null)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [previewMimeType, setPreviewMimeType] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let blobUrlToCleanup: string | null = null

    const loadDocumentAndPreview = async () => {
      try {
        setLoading(true)
        const doc = await documentService.getDocument(documentId)
        setDocument(doc)

        const isImage = doc.mime_type?.startsWith('image/') || false
        const blob = await documentService.downloadDocument(documentId, isImage)
        const url = URL.createObjectURL(blob)
        blobUrlToCleanup = url
        setBlobUrl(url)
        setPreviewMimeType(blob.type || doc.mime_type || null)
        setLoading(false)
      } catch (error) {
        setLoading(false)
      }
    }

    loadDocumentAndPreview()

    return () => {
      if (blobUrlToCleanup) {
        URL.revokeObjectURL(blobUrlToCleanup)
      }
    }
  }, [documentId])

  if (loading) {
    return (
      <div className="preview-loading">
        <Spinner animation="border" />
      </div>
    )
  }

  if (!document || !blobUrl) {
    return (
      <div className="preview-error">
        <p>Error loading document preview</p>
      </div>
    )
  }

  const effectiveMimeType = previewMimeType || document.mime_type || ''
  const isPdf = effectiveMimeType === 'application/pdf'
  const isImage = effectiveMimeType.startsWith('image/')

  return (
    <div className="document-preview-fullscreen">
      {isPdf ? (
        <iframe src={blobUrl} title={document.title} className="preview-iframe" />
      ) : isImage ? (
        <img src={blobUrl} alt={document.title} className="preview-image" />
      ) : (
        <object data={blobUrl} className="preview-iframe" type={effectiveMimeType}>
          <p>Cannot preview this document type</p>
        </object>
      )}
    </div>
  )
}

export default DocumentPreview
